import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, StyledTextInput, TableSkeletonsByAmount } from "@components";
import { useOrderTemplates } from "@data";
import { Customer, OrderTemplate } from "../../models";
import { listRowHover } from "@design-system";

export interface TemplatePickerModalProps {
  show: boolean;
  onSelect: (template: OrderTemplate) => void;
  onClose: () => void;
}

/**
 * Modal that lists the workspace's order templates and lets the user pick one
 * to prefill the create-order wizard ("Start with template").
 */
export const TemplatePickerModal: FC<TemplatePickerModalProps> = (props) => {
  const { t } = useTranslation(["orders", "common"]);
  const [search, setSearch] = useState("");
  const templatesQuery = useOrderTemplates();
  const isLoading = templatesQuery.isLoading || templatesQuery.isRefetching;

  const filtered = useMemo(() => {
    const templates = templatesQuery.data ?? [];
    const term = search.trim().toLowerCase();
    const matched = term
      ? templates.filter((tpl) => tpl.name.toLowerCase().includes(term))
      : templates;
    return [...matched].sort((a, b) => a.name.localeCompare(b.name));
  }, [templatesQuery.data, search]);

  const handleSelect = (template: OrderTemplate) => {
    props.onSelect(template);
    props.onClose();
  };

  const content = (
    <div className="space-y-3">
      <StyledTextInput
        id="template-picker-search"
        value={search}
        placeholder={t("orders:templates.picker.search_ph")}
        onInputChanged={setSearch}
      />

      {isLoading && <TableSkeletonsByAmount quantity={4} />}

      {!isLoading && filtered.length === 0 && (
        <p className="p-4 text-center text-muted">
          {t("orders:templates.picker.no_results")}
        </p>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="max-h-80 overflow-y-auto divide-y divide-border rounded-lg border border-border">
          {filtered.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              onClick={() => handleSelect(tpl)}
              aria-label={t("orders:templates.picker.select_aria", {
                name: tpl.name,
              })}
              className={`w-full text-left p-3 ${listRowHover}`}
            >
              <div className="font-medium text-text">{tpl.name}</div>
              <div className="text-sm text-muted">
                {Customer.getCustomerName(tpl.customer) ||
                  t("common:unknown_customer")}
                {" · "}
                {tpl.route?.length ?? 0} {t("orders:templates.stops")}
                {" · "}
                {tpl.cargo?.length ?? 0} {t("orders:templates.products")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      title={t("orders:templates.picker.title")}
      show={props.show}
      size="lg"
      dismissible
      hideButtons
      content={content}
      toggleModal={props.onClose}
    />
  );
};

export default TemplatePickerModal;
